import { Shape, ShapeOptions } from "../Shape";
import { SVGParsedPathData } from "../../../svg/";
import { YZCanvasContext } from "../canvas";
import { polar } from "@zyzgroup/core-common";

export type PointsShapeOptions = {
  points?: { x: number; y: number }[]; // 相对于自身（0,0）的坐标
  isStraight?: boolean;
} & ShapeOptions;

// 封闭式多边形
// Polygon
// Polyline
// Triangle
export class PointsShape extends Shape {
  private points: { x: number; y: number }[];
  public isStraight: boolean;

  constructor(options?: PointsShapeOptions) {
    super(options);
    this.points = options?.points || [];
    this.isStraight =
      typeof options?.isStraight == "boolean" ? options.isStraight : true;
    this.setAttr({
      strokeWidth: this.getAttr("strokeWidth") || "1",
      stroke: this.getAttr("stroke") || "#000000"
    });
  }

  protected cloneShape(): PointsShape {
    const shape = new PointsShape({
      ...super.getBaseOptions(),
      points: [...this.points],
      isStraight: this.isStraight
    });
    return shape;
  }

  // 供外部变更
  public update(options: Partial<PointsShapeOptions>) {
    if ("points" in options) {
      this.points = [...(options.points || this.points)];
      this.shouldUpdatePathD = true;
    }
    if (typeof options.isStraight == "boolean") {
      this.isStraight = options.isStraight;
      this.shouldUpdatePathD = true;
    }
  }

  public toSVGPathData(): SVGParsedPathData[] {
    const data: SVGParsedPathData[] = [];
    if (this.points.length < 3) {
      // throw new Error("PointsShape.points length must >= 3");
      return data;
    }
    const numPoints = this.points.length;
    if (this.isStraight) {
      data.push(["m", this.points[0].x, this.points[0].y]);
      for (let i = 1; i <= numPoints - 1; i++) {
        data.push([
          "l",
          this.points[i].x - this.points[i - 1].x,
          this.points[i].y - this.points[i - 1].y
        ]);
      }
      data.push(["z"]);
    } else {
      // find the first midpoint and move to it
      const ctrlPoint1X = (this.points[0].x + this.points[numPoints - 1].x) / 2;
      const ctrlPoint1Y = (this.points[0].y + this.points[numPoints - 1].y) / 2;
      let prevX = ctrlPoint1X;
      let prevY = ctrlPoint1Y;
      data.push(["m", prevX, prevY]);
      // curve through the rest, stopping at each midpoint
      for (let i = 0; i < numPoints - 1; i++) {
        const ctrlPointX = (this.points[i].x + this.points[i + 1].x) / 2;
        const ctrlPointY = (this.points[i].y + this.points[i + 1].y) / 2;
        data.push([
          "q",
          this.points[i].x - prevX,
          this.points[i].y - prevY,
          ctrlPointX - prevX,
          ctrlPointY - prevY
        ]);
        prevX = ctrlPointX;
        prevY = ctrlPointY;
      }
      // curve through the last point, back to the first midpoint
      data.push([
        "q",
        this.points[numPoints - 1].x - prevX,
        this.points[numPoints - 1].y - prevY,
        ctrlPoint1X - prevX,
        ctrlPoint1Y - prevY
      ]);
    }
    return data;
  }

  public toCanvasContext(ctx: YZCanvasContext): void {
    if (this.points.length < 3) {
      // throw new Error("PointsShape.points length must >= 3");
      return;
    }
    ctx.beginPath();
    const numPoints = this.points.length;
    if (this.isStraight) {
      ctx.moveTo(this.points[0].x, this.points[0].y);
      for (let i = 1; i <= numPoints - 1; i++) {
        ctx.lineTo(this.points[i].x, this.points[i].y);
      }
      ctx.closePath();
    } else {
      // find the first midpoint and move to it
      const ctrlPoint1X = (this.points[0].x + this.points[numPoints - 1].x) / 2;
      const ctrlPoint1Y = (this.points[0].y + this.points[numPoints - 1].y) / 2;
      ctx.moveTo(ctrlPoint1X, ctrlPoint1Y);
      // curve through the rest, stopping at each midpoint
      for (let i = 0; i < numPoints - 1; i++) {
        const ctrlPointX = (this.points[i].x + this.points[i + 1].x) / 2;
        const ctrlPointY = (this.points[i].y + this.points[i + 1].y) / 2;
        ctx.quadraticCurveTo(
          this.points[i].x,
          this.points[i].y,
          ctrlPointX,
          ctrlPointY
        );
      }
      // curve through the last point, back to the first midpoint
      ctx.quadraticCurveTo(
        this.points[numPoints - 1].x,
        this.points[numPoints - 1].y,
        ctrlPoint1X,
        ctrlPoint1Y
      );
    }
  }

  public isPointInShape(point: { x: number; y: number }) {
    const count = this.points.length;
    let isInside = false;
    const precision = 2e-10;

    for (let i = 0, j = count - 1; i < count; j = i, i++) {
      const x1 = this.points[i].x;
      const y1 = this.points[i].y;
      const x2 = this.points[j].x;
      const y2 = this.points[j].y;

      if (
        (x1 === point.x && y1 === point.y) ||
        (x2 === point.x && y2 === point.y)
      ) {
        return true;
      }

      if (point.y === y1 && point.y === y2) {
        return true;
      }

      if ((point.y >= y1 && point.y < y2) || (point.y < y1 && point.y >= y2)) {
        const k = (x2 - x1) / (y2 - y1);
        const _x = x1 + k * (point.y - y1);

        if (_x === point.x) {
          return true;
        }

        if (Math.abs(_x - point.x) < precision) {
          return true;
        }

        if (_x > point.x) {
          isInside = !isInside;
        }
      }
    }

    return isInside;
  }

  public [Symbol.iterator]() {
    let index = -1;
    return {
      next: () => ({
        value:
          index <= this.points.length - 1 ? this.points[++index] : undefined,
        done: index >= this.points.length
      })
    };
  }

  public static generate(
    centerX: number,
    centerY: number,
    sideDistance: number, // 中心到边的直线距离
    sideCount: number
  ): PointsShape {
    const points: { x: number; y: number }[] = [];
    for (let i = 0; i < sideCount; i++) {
      const theta = (i / sideCount) * 2 * Math.PI;
      const { dx, dy } = polar(theta, sideDistance);
      points.push({ x: centerX + dx, y: centerY + dy });
    }
    return new PointsShape({ points });
  }
}

export default PointsShape;
